import * as React from "react";
import {
  View,
  Text,
  Flex,
  Heading,
  Tabs,
  TabList,
  TabPanels,
  Item,
  ActionButton,
  RadioGroup,
  Radio,
} from "@adobe/react-spectrum";
import { ToastQueue } from "@react-spectrum/toast";
import { ICustomViewData } from "../../CustomViewDialog";
import { useLanguageDataLoader } from "../../../data-components/Contexts/DataLoader";
import { ISortingRow } from "../SortingTable";
import { IFilterControl, IFilterItem } from "../FilterSetupPlaceholder";
import { Profile } from "../../../../../services/soap/access/responses/ListProfileResponse";
import { CopyIcon } from "../../../Icons/IconsLib";
import styles from "./Stage4Summary.module.css";

type ParsedRange = {
  from?: string | number;
  to?: string | number;
  currencyType?: string;
  start?: string | number;
  end?: string | number;
};

const RELATIVE_DATE_PRESET_LABELS: Record<string, string> = {
  today: "Today",
  yesterday: "Yesterday",
  tomorrow: "Tomorrow",
  last_7_days: "Last 7 Days",
  next_7_days: "Next 7 Days",
  last_30_days: "Last 30 Days",
  next_30_days: "Next 30 Days",
  last_month: "Last Month",
  this_month: "This Month",
  next_month: "Next Month",
  last_quarter: "Last Quarter",
  this_quarter: "This Quarter",
  next_quarter: "Next Quarter",
  last_year: "Last Year",
  this_year: "This Year",
  next_year: "Next Year",
};

const BOOLEAN_VALUE_LABELS: Record<string, string> = {
  yes: "Yes",
  no: "No",
  empty: "Empty",
};

const toTitleCase = (value?: string) => {
  if (!value) return "Not set";
  return value
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

const safeParseRange = (value?: string): ParsedRange => {
  if (!value) return {};
  try {
    return JSON.parse(value) as ParsedRange;
  } catch {
    return {};
  }
};

const formatCsvList = (value?: string) => {
  if (!value) return "No values selected";
  const items = value
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);
  return items.length > 0 ? items.join(", ") : "No values selected";
};

const formatFilterItemValue = (
  filter: IFilterControl,
  filterItem: IFilterItem
) => {
  const operatorLabel = toTitleCase(filterItem.type);
  let valueLabel = "";

  // Check if this is a current user filter
  if (filterItem.isCurrentUser) {
    valueLabel = "Current user";
    return valueLabel ? `${operatorLabel}: ${valueLabel}` : operatorLabel;
  }

  var data_type = filter.dataType;
  if (data_type.includes("|")) {
    data_type = data_type.split("|")[0];
  }
  switch (data_type) {
    case "numeric": {
      if (filterItem.type === "between") {
        const { from, to } = safeParseRange(filterItem.value);
        valueLabel = `${from ?? "-"} to ${to ?? "-"}`;
      } else {
        valueLabel = filterItem.value ?? "";
      }
      break;
    }
    case "currency": {
      if (filterItem.type === "between") {
        const { from, to, currencyType } = safeParseRange(filterItem.value);
        const rangeText = `${from ?? "-"} to ${to ?? "-"}`;
        valueLabel = currencyType
          ? `${rangeText} (${currencyType})`
          : rangeText;
      } else {
        valueLabel = filterItem.value ?? "";
      }
      break;
    }
    case "date": {
      if (filterItem.type === "between") {
        const { start, end } = safeParseRange(filterItem.value);
        valueLabel = `${start ?? "-"} to ${end ?? "-"}`;
      } else if (filterItem.type === "relative_preset") {
        valueLabel =
          RELATIVE_DATE_PRESET_LABELS[filterItem.value ?? ""] ??
          filterItem.value ??
          "";
      } else {
        valueLabel = filterItem.value ?? "";
      }
      break;
    }
    case "boolean": {
      valueLabel =
        BOOLEAN_VALUE_LABELS[filterItem.value ?? ""] ??
        toTitleCase(filterItem.value);
      break;
    }
    case "folder":
    case "project": {
      valueLabel = filterItem.valueLabel || filterItem.value || "";
      break;
    }
    case "folder_status":
    case "project_status":
    case "activity_status":
    case "folder_type":
    case "project_type":
    case "activity_type":
    case "activity_type|text": {
      // Use valueLabel if available, otherwise fall back to value (IDs)
      valueLabel = formatCsvList(filterItem.valueLabel || filterItem.value);
      break;
    }
    default: {
      valueLabel = filterItem.valueLabel || filterItem.value || "";
    }
  }

  return valueLabel ? `${operatorLabel}: ${valueLabel}` : operatorLabel;
};

const formatFilterValues = (filter: IFilterControl) => {
  const filterItems = filter.filterInfo ?? [];
  if (filterItems.length === 0) {
    return ["No criteria set"];
  }

  return filterItems.map((filterItem) =>
    formatFilterItemValue(filter, filterItem)
  );
};

export interface IStage4SummaryProps {
  data: ICustomViewData;
  profiles: Profile[];
  onDataChange?: (data: ICustomViewData) => void;
}

export function Stage4Summary(props: IStage4SummaryProps) {
  const { data, profiles, onDataChange } = props;

  // Load language data
  const { data: languages } = useLanguageDataLoader();

  // Create a map of languageId -> languageLong for quick lookup
  const languageMap = React.useMemo(() => {
    const map = new Map<string, string>();
    languages.forEach((lang) => {
      // Store both as string and number key since languageId might be stored as string
      map.set(String(lang.id), lang.languageLong);
      map.set(lang.id.toString(), lang.languageLong);
    });
    return map;
  }, [languages]);

  // Count total selected columns
  const selectedColumnsCount = React.useMemo(() => {
    return data.columnsSetup.selectedColumns.reduce((count, section) => {
      return count + (section.items?.length || 0);
    }, 0);
  }, [data.columnsSetup.selectedColumns]);

  const formattedFilters = React.useMemo(
    () =>
      data.filterSetup.map((filter) => ({
        itemname: filter.data.itemname,
        name: filter.name,
        descriptions: formatFilterValues(filter),
      })),
    [data.filterSetup]
  );

  const summaryContent = (
    <Flex direction="column" gap="size-200">
      <Heading level={3}>Review Your Custom View</Heading>

      <View UNSAFE_className={styles.summarySection}>
        <Heading level={4}>Columns Setup</Heading>

        <View UNSAFE_className={styles.summaryItem}>
          <Text UNSAFE_className={styles.label}>Names:</Text>
          <Flex direction="column" gap="size-50" marginStart="size-100">
            {data.columnsSetup.names.map((nameInfo, index) => {
              const languageName = nameInfo.languageId
                ? languageMap.get(nameInfo.languageId) || nameInfo.languageId
                : null;
              return (
                <Text key={index}>
                  {nameInfo.name || "(Unnamed)"}{" "}
                  {languageName ? `(${languageName})` : ""}
                </Text>
              );
            })}
          </Flex>
        </View>

        <View UNSAFE_className={styles.summaryItem}>
          <Text UNSAFE_className={styles.label}>Selected Columns:</Text>
          <Text marginStart="size-100">{selectedColumnsCount} column(s)</Text>
        </View>

        <View UNSAFE_className={styles.summaryItem}>
          <Text UNSAFE_className={styles.label}>View Access:</Text>
          <Text marginStart="size-100" UNSAFE_className={styles.capitalize}>
            {data.columnsSetup.viewAccess === "profile" &&
            data.columnsSetup.selectedProfileId
              ? (() => {
                  const profile = profiles.find(
                    (p) => p.id === data.columnsSetup.selectedProfileId
                  );
                  return profile
                    ? `Profile: ${profile.name}`
                    : "Profile: Not selected";
                })()
              : data.columnsSetup.viewAccess}
          </Text>
        </View>
      </View>

      <View UNSAFE_className={styles.summarySection}>
        <Heading level={4}>Filter Setup</Heading>
        {formattedFilters.length === 0 ? (
          <Text UNSAFE_className={styles.placeholderText}>No data entered</Text>
        ) : (
          <Flex direction="column" gap="size-100" marginTop="size-100">
            {formattedFilters.map((filter, index) => (
              <View
                key={filter.itemname || index}
                UNSAFE_className={styles.summaryItem}
              >
                <Text UNSAFE_className={styles.label}>
                  {index + 1}. {filter.name}
                </Text>
                <Flex direction="column" gap="size-50" marginStart="size-100">
                  {filter.descriptions.map((description, descriptionIndex) => (
                    <Text
                      key={`${filter.itemname || index}-${descriptionIndex}`}
                    >
                      {description}
                    </Text>
                  ))}
                </Flex>
              </View>
            ))}
          </Flex>
        )}
      </View>

      <View UNSAFE_className={styles.summarySection}>
        <Heading level={4}>Sorting Setup</Heading>
        {data.sortingSetup && data.sortingSetup.length > 0 ? (
          <Flex direction="column" gap="size-100" marginTop="size-100">
            {data.sortingSetup.map((row: ISortingRow, index: number) => (
              <View
                key={row.itemname || index}
                UNSAFE_className={styles.summaryItem}
              >
                <Text>
                  {index + 1}. {row.column} - {row.level}
                </Text>
              </View>
            ))}
          </Flex>
        ) : (
          <Text UNSAFE_className={styles.placeholderText}>No data entered</Text>
        )}
      </View>

      <View UNSAFE_className={styles.summarySection}>
        <Heading level={4}>Preferred Layout</Heading>

        <View UNSAFE_className={styles.summaryItem}>
          <Text UNSAFE_className={styles.label}>Desktop:</Text>
          <Flex direction="column" gap="size-50" marginStart="size-100">
            <RadioGroup
              orientation="horizontal"
              value={data.columnsSetup.desktopOrientation}
              onChange={(value) => {
                if (onDataChange) {
                  onDataChange({
                    ...data,
                    columnsSetup: {
                      ...data.columnsSetup,
                      desktopOrientation: value as "Detail" | "Full",
                    },
                  });
                }
              }}
            >
              <Radio value="Detail">Table View</Radio>
              <Radio value="Full">Cards View</Radio>
            </RadioGroup>
          </Flex>
        </View>

        <View UNSAFE_className={styles.summaryItem}>
          <Text UNSAFE_className={styles.label}>Tablet:</Text>
          <Flex direction="column" gap="size-50" marginStart="size-100">
            <RadioGroup
              orientation="horizontal"
              value={data.columnsSetup.tabletOrientation}
              onChange={(value) => {
                if (onDataChange) {
                  onDataChange({
                    ...data,
                    columnsSetup: {
                      ...data.columnsSetup,
                      tabletOrientation: value as "Detail" | "Full",
                    },
                  });
                }
              }}
            >
              <Radio value="Detail">Table View</Radio>
              <Radio value="Full">Cards View</Radio>
            </RadioGroup>
          </Flex>
        </View>
      </View>
    </Flex>
  );

  const jsonString = React.useMemo(() => JSON.stringify(data, null, 2), [data]);

  const handleCopyToClipboard = React.useCallback(async () => {
    try {
      await navigator.clipboard.writeText(jsonString);
      ToastQueue.positive("JSON copied to clipboard", { timeout: 2000 });
    } catch (err) {
      ToastQueue.negative("Failed to copy JSON", { timeout: 2000 });
    }
  }, [jsonString]);

  return (
    <View UNSAFE_className={styles.stage4Summary}>
      <Tabs aria-label="Custom view summary" defaultSelectedKey="summary">
        <TabList>
          <Item key="summary">Summary</Item>
          <Item key="json">JSON</Item>
        </TabList>
        <TabPanels>
          <Item key="summary">{summaryContent}</Item>
          <Item key="json">
            <View UNSAFE_className={styles.jsonContainer}>
              <ActionButton
                onPress={handleCopyToClipboard}
                UNSAFE_className={styles.copyButton}
                aria-label="Copy JSON to clipboard"
              >
                <CopyIcon size={16} />
              </ActionButton>
              <View UNSAFE_className={styles.summarySection}>
                <pre className={styles.jsonBlock}>{jsonString}</pre>
              </View>
            </View>
          </Item>
        </TabPanels>
      </Tabs>
    </View>
  );
}
